import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Avatar,
  TablePagination,
  Rating,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import MapIcon from '@mui/icons-material/Map';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../../services/api';
import { useConfirm, useToast } from '../../contexts/ConfirmContext';
import TestRoutesMap from '../../components/TestRoutesMap';

interface Coordinate {
  lat: number;
  lng: number;
}

interface UserItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
  isActive: boolean;
}

interface AdminPayment {
  id: number;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  notes: string | null;
  isPaid: boolean;
  expiryDate: string | null;
  transactionId: string | null;
  paymentStatus: string;
}

interface InstructorSubscription {
  instructorId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  subscriptionStatus: string; // "Active" | "Expired" | "None"
  subscriptionType: string; // "Trial" | "Monthly" | "Annual" | "None"
  expiryDate: string | null;
  hasWhatsAppAddon: boolean;
  lastPaymentAmount: number | null;
  lastPaymentDate: string | null;
  lastPaymentMethod: string | null;
  isClaimed: boolean;
  claimToken?: string | null;
  paymentHistory?: AdminPayment[];
}

interface DashboardStats {
  totalUsers: number;
  totalInstructors: number;
  totalStudents: number;
  activeUsers: number;
  inactiveUsers: number;
}

const getImageUrl = (path: string | null) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = process.env.REACT_APP_API_URL || 'https://localhost:7245/api';
  return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

const AdminDashboard: React.FC = () => {
  const confirm = useConfirm();
  const showToast = useToast();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<InstructorSubscription[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Test Routes states
  const [adminRoutes, setAdminRoutes] = useState<any[]>([]);
  const [recordedCoords, setRecordedCoords] = useState<Coordinate[]>([]);
  const [routeName, setRouteName] = useState('');
  const [routeDesc, setRouteDesc] = useState('');
  const [routeCenter, setRouteCenter] = useState('');
  const [routeStart, setRouteStart] = useState('');
  const [creatingRoute, setCreatingRoute] = useState(false);

  // Bulk Import state
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  // Bulk Email Campaign states
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<string[]>([]);
  const [profileStatusFilter, setProfileStatusFilter] = useState<'All' | 'Claimed' | 'Unclaimed'>('All');
  const [sendingEmails, setSendingEmails] = useState(false);

  const fetchAdminRoutes = async () => {
    try {
      const res = await api.getTestRoutes();
      setAdminRoutes(res.data);
    } catch (err) {
      console.error('Error fetching admin test routes:', err);
    }
  };

  const handleCreateRoute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!routeName.trim() || !routeCenter.trim() || !routeStart.trim() || recordedCoords.length === 0) {
      showToast('Please fill all fields and record at least one point on the map grid.', 'error');
      return;
    }

    try {
      setCreatingRoute(true);
      await api.createTestRoute({
        routeName: routeName.trim(),
        description: routeDesc.trim(),
        centerName: routeCenter.trim(),
        startLocation: routeStart.trim(),
        coordinatesJson: JSON.stringify(recordedCoords),
      });

      showToast('Test route created successfully!', 'success');
      // Reset form
      setRouteName('');
      setRouteDesc('');
      setRouteCenter('');
      setRouteStart('');
      setRecordedCoords([]);
      fetchAdminRoutes();
    } catch (err: any) {
      console.error('Error creating route:', err);
      showToast(err.response?.data?.message || 'Failed to create test route.', 'error');
    } finally {
      setCreatingRoute(false);
    }
  };

  const handleDeleteRoute = async (id: number) => {
    if (await confirm({ title: 'Delete Test Route', message: 'Are you sure you want to delete this test route? This cannot be undone.' })) {
      try {
        await api.deleteTestRoute(id);
        showToast('Test route deleted successfully!', 'success');
        fetchAdminRoutes();
      } catch (err: any) {
        console.error('Error deleting route:', err);
        showToast('Failed to delete test route.', 'error');
      }
    }
  };
  
  // Navigation & filter states
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  // Pagination states
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10);
  const [subsPage, setSubsPage] = useState(0);
  const [subsRowsPerPage, setSubsRowsPerPage] = useState(10);
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [paymentsRowsPerPage, setPaymentsRowsPerPage] = useState(10);

  // Reset page numbers on tab or search change
  useEffect(() => {
    setUsersPage(0);
    setSubsPage(0);
    setPaymentsPage(0);
  }, [searchTerm, activeTab]);
  
  // Pending bank transfers states
  const [pendingPayments, setPendingPayments] = useState<any[]>([]);
  const [approvingPaymentId, setApprovingPaymentId] = useState<number | null>(null);

  // Edit subscription states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<InstructorSubscription | null>(null);
  const [subType, setSubType] = useState<string>('None');
  const [subExpiryDate, setSubExpiryDate] = useState<string>('');
  const [hasWhatsApp, setHasWhatsApp] = useState<boolean>(false);
  const [subNotes, setSubNotes] = useState<string>('');
  const [savingSub, setSavingSub] = useState(false);

  // Testimonials moderation states
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);

  const fetchTestimonials = async () => {
    try {
      setTestimonialsLoading(true);
      const res = await api.getAdminTestimonials();
      setTestimonials(res.data);
    } catch (err) {
      console.error('Error fetching admin testimonials:', err);
      showToast('Failed to load testimonials.', 'error');
    } finally {
      setTestimonialsLoading(false);
    }
  };

  const handleApproveTestimonial = async (id: number) => {
    try {
      await api.approveTestimonial(id);
      showToast('Testimonial approved successfully!', 'success');
      await fetchTestimonials();
    } catch (err) {
      console.error('Error approving testimonial:', err);
      showToast('Failed to approve testimonial.', 'error');
    }
  };

  const handleRejectTestimonial = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Reject Testimonial',
      message: 'Are you sure you want to reject and remove this testimonial feedback?',
      severity: 'warning'
    });
    if (!isConfirmed) return;

    try {
      await api.rejectTestimonial(id);
      showToast('Testimonial rejected and deleted successfully.', 'success');
      await fetchTestimonials();
    } catch (err) {
      console.error('Error rejecting testimonial:', err);
      showToast('Failed to reject testimonial.', 'error');
    }
  };

  useEffect(() => {
    if (activeTab === 3) {
      fetchTestimonials();
    } else if (activeTab === 4) {
      fetchAdminRoutes();
    }
  }, [activeTab]);

  // User Details Modal states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState<any>(null);
  const [detailsActiveTab, setDetailsActiveTab] = useState(0);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      setImportText(text);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImportError(null);
    setImportSuccess(null);
    
    if (!importText.trim()) {
      setImportError("Please paste JSON/CSV data or select a file first.");
      return;
    }

    try {
      setImporting(true);
      let parsedData: any[] = [];

      try {
        parsedData = JSON.parse(importText);
      } catch (e: any) {
        // Try parsing as CSV if JSON parsing fails
        const lines = importText.split('\n').filter(l => l.trim());
        if (lines.length > 1) {
          const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          for (let i = 1; i < lines.length; i++) {
            const currentLine = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
            const obj: any = {};
            headers.forEach((header, index) => {
              const val = currentLine[index];
              if (header === 'outcodes' || header === 'Outcodes') {
                obj.outcodes = val ? val.split(';').map(o => o.trim()) : [];
              } else if (header === 'baseHourlyRate' || header === 'BaseHourlyRate') {
                obj.baseHourlyRate = val ? parseFloat(val) : 25.00;
              } else {
                // map header to camelCase
                const camelHeader = header.charAt(0).toLowerCase() + header.slice(1);
                obj[camelHeader] = val;
              }
            });
            parsedData.push(obj);
          }
        } else {
          throw new Error("Invalid JSON format and not enough lines for CSV parsing.");
        }
      }

      if (!Array.isArray(parsedData) || parsedData.length === 0) {
        throw new Error("Import data must be a non-empty array of driving instructors.");
      }

      const response = await api.importInstructors(parsedData);
      
      // Let's count success
      const results = response.data;
      const successes = results.filter((r: any) => r.success);
      const failures = results.filter((r: any) => !r.success);

      let msg = `Successfully imported ${successes.length} instructors.`;
      if (failures.length > 0) {
        msg += ` Failed to import ${failures.length} (already exist or invalid data).`;
      }
      setImportSuccess(msg);
      setImportText('');
      await fetchData(); // refresh data
    } catch (err: any) {
      console.error("Failed to import instructors:", err);
      setImportError(err.message || "Import failed. Please check the format.");
    } finally {
      setImporting(false);
    }
  };

  const handleSendBulkEmails = async () => {
    if (selectedInstructorIds.length === 0) {
      showToast("Please select at least one instructor first.", "warning");
      return;
    }
    
    const confirmed = await confirm({
      title: 'Send Claim Invitations',
      message: `Are you sure you want to send claim invitation emails to the ${selectedInstructorIds.length} selected driving instructors?`,
      confirmText: 'Send Emails',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      setSendingEmails(true);
      const response = await api.sendClaimEmails(selectedInstructorIds);
      
      const results = response.data;
      const successes = results.filter((r: any) => r.success);
      const failures = results.filter((r: any) => !r.success);
      
      let msg = `Successfully sent claim emails to ${successes.length} instructors.`;
      if (failures.length > 0) {
        msg += ` Failed to send to ${failures.length}.`;
      }
      showToast(msg, failures.length > 0 ? 'warning' : 'success');
      setSelectedInstructorIds([]);
      await fetchData();
    } catch (err: any) {
      console.error("Failed to send claim emails:", err);
      showToast(err.response?.data?.message || "Failed to send claim emails. Please check SMTP settings.", 'error');
    } finally {
      setSendingEmails(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [usersRes, statsRes, pendingPaymentsRes, subsRes] = await Promise.all([
        api.getUsers(),
        api.getAdminDashboard(),
        api.getPendingPayments(),
        api.getInstructorsSubscriptions()
      ]);

      const usersList = usersRes.data.data || usersRes.data;
      const statsData = statsRes.data.data || statsRes.data;
      const pendingList = pendingPaymentsRes.data;
      const subsList = subsRes.data;

      setUsers(Array.isArray(usersList) ? usersList : []);
      setStats(statsData);
      setPendingPayments(Array.isArray(pendingList) ? pendingList : []);
      setSubscriptions(Array.isArray(subsList) ? subsList : []);
    } catch (err: any) {
      console.error('Error fetching admin dashboard data:', err);
      setError('An error occurred while loading data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApprovePayment = async (paymentId: number) => {
    const isConfirmed = await confirm({
      title: 'Approve Bank Transfer',
      message: 'Are you sure you have verified this transfer in your HSBC UK bank account and want to approve it? This will immediately activate the user\'s subscription.',
      severity: 'info'
    });
    if (!isConfirmed) return;

    try {
      setApprovingPaymentId(paymentId);
      await api.approvePayment(paymentId);
      showToast('Payment approved and subscription activated successfully!', 'success');
      await fetchData();
    } catch (err) {
      console.error('Error approving payment:', err);
      showToast('An error occurred while approving the payment.', 'error');
    } finally {
      setApprovingPaymentId(null);
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    const isConfirmed = await confirm({
      title: currentStatus ? 'Deactivate User' : 'Activate User',
      message: `Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this user?`,
      severity: 'warning'
    });
    if (!isConfirmed) return;

    try {
      setUpdatingUserId(userId);
      if (currentStatus) {
        await api.deactivateUser(userId);
      } else {
        await api.activateUser(userId);
      }
      
      setUsers(prevUsers =>
        prevUsers.map(u => (u.id === userId ? { ...u, isActive: !currentStatus } : u))
      );
      
      showToast('User status updated successfully.', 'success');
      
      if (stats) {
        setStats({
          ...stats,
          activeUsers: currentStatus ? stats.activeUsers - 1 : stats.activeUsers + 1,
          inactiveUsers: currentStatus ? stats.inactiveUsers + 1 : stats.inactiveUsers - 1
        });
      }
    } catch (err) {
      console.error('Error toggling user status:', err);
      showToast('An error occurred while updating user status.', 'error');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleOpenEditDialog = (sub: InstructorSubscription) => {
    setEditingSub(sub);
    setSubType(sub.subscriptionType);
    setSubExpiryDate(sub.expiryDate ? sub.expiryDate.split('T')[0] : '');
    setHasWhatsApp(sub.hasWhatsAppAddon);
    setSubNotes('');
    setDialogOpen(true);
  };

  const handleOpenDetails = async (userId: string) => {
    try {
      setSelectedUserDetails(null);
      setDetailsActiveTab(0);
      setDetailsDialogOpen(true);
      setDetailsLoading(true);
      
      const res = await api.getUserDetails(userId);
      setSelectedUserDetails(res.data);
    } catch (err) {
      console.error('Error fetching user details:', err);
      showToast('An error occurred while loading user details.', 'error');
      setDetailsDialogOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSaveSubscription = async () => {
    if (!editingSub) return;

    try {
      setSavingSub(true);
      await api.updateInstructorSubscription({
        userId: editingSub.userId,
        subscriptionType: subType,
        expiryDate: subExpiryDate ? new Date(subExpiryDate).toISOString() : undefined,
        hasWhatsAppAddon: hasWhatsApp,
        notes: subNotes || `Adjusted by administrator`
      });

      showToast('Subscription updated successfully!', 'success');
      setDialogOpen(false);
      await fetchData();
    } catch (err) {
      console.error('Error saving subscription:', err);
      showToast('An error occurred while updating the subscription.', 'error');
    } finally {
      setSavingSub(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    const query = searchTerm.toLowerCase();
    return fullName.includes(query) || email.includes(query) || user.userType.toLowerCase().includes(query);
  });

  const paginatedUsers = filteredUsers.slice(
    usersPage * usersRowsPerPage,
    usersPage * usersRowsPerPage + usersRowsPerPage
  );

  const filteredSubscriptions = subscriptions.filter(sub => {
    const fullName = `${sub.firstName} ${sub.lastName}`.toLowerCase();
    const email = sub.email.toLowerCase();
    const query = searchTerm.toLowerCase();
    const matchesSearch = fullName.includes(query) || email.includes(query) || sub.subscriptionType.toLowerCase().includes(query) || sub.subscriptionStatus.toLowerCase().includes(query);
    
    if (profileStatusFilter === 'Claimed') {
      return matchesSearch && sub.isClaimed;
    } else if (profileStatusFilter === 'Unclaimed') {
      return matchesSearch && !sub.isClaimed;
    }
    return matchesSearch;
  });

  const paginatedSubscriptions = filteredSubscriptions.slice(
    subsPage * subsRowsPerPage,
    subsPage * subsRowsPerPage + subsRowsPerPage
  );

  const allPayments = subscriptions.flatMap(sub => 
    (sub.paymentHistory || []).map(p => ({
      ...p,
      instructorName: `${sub.firstName} ${sub.lastName}`,
      instructorEmail: sub.email
    }))
  ).sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());

  const filteredPayments = allPayments.filter(p => {
    const query = searchTerm.toLowerCase();
    return (
      p.instructorName.toLowerCase().includes(query) ||
      p.instructorEmail.toLowerCase().includes(query) ||
      (p.transactionId && p.transactionId.toLowerCase().includes(query)) ||
      p.paymentMethod.toLowerCase().includes(query) ||
      (p.notes && p.notes.toLowerCase().includes(query))
    );
  });

  const paginatedPayments = filteredPayments.slice(
    paymentsPage * paymentsRowsPerPage,
    paymentsPage * paymentsRowsPerPage + paymentsRowsPerPage
  );

  // Client-calculated summary statistics
  const activePremiumCount = subscriptions.filter(s => s.subscriptionStatus === 'Active' && (s.subscriptionType === 'Monthly' || s.subscriptionType === 'Annual')).length;
  const activeTrialCount = subscriptions.filter(s => s.subscriptionStatus === 'Active' && s.subscriptionType === 'Trial').length;
  const totalRevenue = subscriptions.reduce((sum, sub) => {
    const subSum = (sub.paymentHistory || [])
      .filter(p => p.paymentStatus === 'Completed')
      .reduce((s, p) => s + p.amount, 0);
    return sum + subSum;
  }, 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
        Admin Management Panel
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Users</Typography>
                  <Typography variant="h3" fontWeight="bold">{stats.totalUsers}</Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Instructors</Typography>
                  <Typography variant="h3" fontWeight="bold">{stats.totalInstructors}</Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Students</Typography>
                  <Typography variant="h3" fontWeight="bold">{stats.totalStudents}</Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: '#7b1fa2', color: '#fff', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Active Premium Plans</Typography>
                  <Typography variant="h3" fontWeight="bold">{activePremiumCount}</Typography>
                </Box>
                <StarIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: '#e65100', color: '#fff', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Active Free Trials</Typography>
                  <Typography variant="h3" fontWeight="bold">{activeTrialCount}</Typography>
                </Box>
                <StarIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ bgcolor: '#2e7d32', color: '#fff', height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>Total Revenue</Typography>
                  <Typography variant="h3" fontWeight="bold">£{totalRevenue.toFixed(2)}</Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold" sx={{ opacity: 0.5 }}>£</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Pending Bank Transfers Table */}
      {pendingPayments.length > 0 && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', mb: 4, border: '1px solid rgba(46, 125, 50, 0.3)', bgcolor: 'rgba(46, 125, 50, 0.01)' }}>
          <Typography variant="h6" fontWeight="bold" color="success.main" mb={2}>
            Pending Bank Transfers ({pendingPayments.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Transaction ID</b></TableCell>
                  <TableCell><b>Instructor ID</b></TableCell>
                  <TableCell><b>Amount</b></TableCell>
                  <TableCell><b>Reference / Notes</b></TableCell>
                  <TableCell><b>Date Submitted</b></TableCell>
                  <TableCell align="right"><b>Action</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingPayments.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell><code>{payment.transactionId}</code></TableCell>
                    <TableCell>{payment.instructorId}</TableCell>
                    <TableCell><b>£{payment.amount.toFixed(2)}</b></TableCell>
                    <TableCell>{payment.notes || 'N/A'}</TableCell>
                    <TableCell>{payment.paymentDate ? new Date(payment.paymentDate).toLocaleString() : 'N/A'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleApprovePayment(payment.id)}
                        disabled={approvingPaymentId === payment.id}
                        sx={{ fontWeight: 'bold' }}
                      >
                        {approvingPaymentId === payment.id ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          'Approve'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tabs Menu */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => { setActiveTab(newValue); setSearchTerm(''); }} aria-label="admin tabs">
          <Tab label="User Accounts" id="tab-0" />
          <Tab label="Instructors & Subscriptions" id="tab-1" />
          <Tab label="Payment & Transaction History" id="tab-2" />
          <Tab label="Testimonials Moderation" id="tab-3" />
          <Tab label="Manage Test Routes" id="tab-4" icon={<MapIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* TAB 0: User Accounts list */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Typography variant="h6" fontWeight="medium">
              User Accounts ({filteredUsers.length} Users)
            </Typography>
            <TextField
              size="small"
              placeholder="Search by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell><b>Name</b></TableCell>
                  <TableCell><b>Email</b></TableCell>
                  <TableCell><b>Role</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell align="right"><b>Action</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No users found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.firstName} {user.lastName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.userType === 'Admin' ? 'Admin' : user.userType === 'Instructor' ? 'Instructor' : 'Student'}
                          color={user.userType === 'Admin' ? 'secondary' : user.userType === 'Instructor' ? 'success' : 'primary'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          color="info"
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          sx={{ mr: 1, fontWeight: 'bold' }}
                        >
                          View Details
                        </Button>
                        <Button
                          size="small"
                          variant={user.isActive ? 'outlined' : 'contained'}
                          color={user.isActive ? 'error' : 'success'}
                          onClick={() => handleToggleStatus(user.id, user.isActive)}
                          disabled={updatingUserId === user.id}
                          startIcon={user.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                        >
                          {updatingUserId === user.id ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : user.isActive ? (
                            'Deactivate'
                          ) : (
                            'Activate'
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={usersRowsPerPage}
            page={usersPage}
            onPageChange={(e, newPage) => setUsersPage(newPage)}
            onRowsPerPageChange={(e) => {
              setUsersRowsPerPage(parseInt(e.target.value, 10));
              setUsersPage(0);
            }}
          />
        </Paper>
      )}

      {/* TAB 1: Instructors & Subscriptions list */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Typography variant="h6" fontWeight="medium">
              Instructor Subscriptions ({filteredSubscriptions.length} Instructors)
            </Typography>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setImportError(null);
                  setImportSuccess(null);
                  setImportText('');
                  setOpenImportDialog(true);
                }}
                sx={{ fontWeight: 'bold', textTransform: 'none' }}
              >
                Import Instructors
              </Button>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="profile-status-filter-label">Profile Status</InputLabel>
                <Select
                  labelId="profile-status-filter-label"
                  value={profileStatusFilter}
                  label="Profile Status"
                  onChange={(e) => {
                    setProfileStatusFilter(e.target.value as any);
                    setSelectedInstructorIds([]);
                  }}
                >
                  <MenuItem value="All">All Profiles</MenuItem>
                  <MenuItem value="Claimed">Claimed Only</MenuItem>
                  <MenuItem value="Unclaimed">Unclaimed Only</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                placeholder="Search by name, email, or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
            </Box>
          </Box>

          {selectedInstructorIds.length > 0 && (
            <Box 
              sx={{ 
                mb: 3, 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: 'rgba(25, 118, 210, 0.04)', 
                border: '1px solid rgba(25, 118, 210, 0.12)',
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                {selectedInstructorIds.length} instructors selected
              </Typography>
              <Box display="flex" gap={1.5}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedInstructorIds([])}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  Deselect All
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSendBulkEmails}
                  disabled={sendingEmails}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  {sendingEmails ? <CircularProgress size={20} color="inherit" /> : `Send Claim Emails (${selectedInstructorIds.length})`}
                </Button>
              </Box>
            </Box>
          )}

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedInstructorIds.length > 0 && 
                        selectedInstructorIds.length < filteredSubscriptions.length
                      }
                      checked={
                        filteredSubscriptions.length > 0 && 
                        selectedInstructorIds.length === filteredSubscriptions.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInstructorIds(filteredSubscriptions.map(s => s.instructorId));
                        } else {
                          setSelectedInstructorIds([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell><b>Instructor</b></TableCell>
                  <TableCell><b>Email</b></TableCell>
                  <TableCell><b>Profile</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Plan Type</b></TableCell>
                  <TableCell><b>Expiry Date</b></TableCell>
                  <TableCell><b>WhatsApp Addon</b></TableCell>
                  <TableCell><b>Last Payment</b></TableCell>
                  <TableCell align="right"><b>Action</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No instructors found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedSubscriptions.map((sub) => (
                    <TableRow key={sub.instructorId} hover selected={selectedInstructorIds.includes(sub.instructorId)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedInstructorIds.includes(sub.instructorId)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedInstructorIds(prev => [...prev, sub.instructorId]);
                            } else {
                              setSelectedInstructorIds(prev => prev.filter(id => id !== sub.instructorId));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell><b>{sub.firstName} {sub.lastName}</b></TableCell>
                      <TableCell>{sub.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={sub.isClaimed ? 'Claimed' : 'Unclaimed'}
                          color={sub.isClaimed ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 'bold', mr: 1 }}
                        />
                        {!sub.isClaimed && sub.claimToken && (
                          <Button
                            size="small"
                            variant="text"
                            color="primary"
                            onClick={() => {
                              const claimUrl = `${window.location.origin}/claim-profile?token=${sub.claimToken}`;
                              navigator.clipboard.writeText(claimUrl);
                              showToast('Claim link copied to clipboard!', 'success');
                            }}
                            sx={{ fontSize: '0.65rem', p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 'bold', textDecoration: 'underline' }}
                          >
                            Copy Link
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sub.subscriptionStatus}
                          color={sub.subscriptionStatus === 'Active' ? 'success' : sub.subscriptionStatus === 'Expired' ? 'error' : 'default'}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sub.subscriptionType}
                          color={sub.subscriptionType === 'Trial' ? 'info' : sub.subscriptionType === 'Annual' ? 'secondary' : sub.subscriptionType === 'Monthly' ? 'primary' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {sub.expiryDate ? new Date(sub.expiryDate).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sub.hasWhatsAppAddon ? 'Enabled' : 'Disabled'}
                          color={sub.hasWhatsAppAddon ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {sub.lastPaymentAmount !== null && sub.lastPaymentAmount !== undefined ? (
                          <Typography variant="body2">
                            <b>£{sub.lastPaymentAmount.toFixed(2)}</b>
                            <br />
                            <small style={{ color: 'gray' }}>
                              {sub.lastPaymentDate ? new Date(sub.lastPaymentDate).toLocaleDateString() : ''} ({sub.lastPaymentMethod})
                            </small>
                          </Typography>
                        ) : (
                          'No Payment'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          onClick={() => navigate(`/admin/users/${sub.userId}`)}
                          sx={{ mr: 1, fontWeight: 'bold' }}
                        >
                          Details
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenEditDialog(sub)}
                          sx={{ fontWeight: 'bold' }}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredSubscriptions.length}
            rowsPerPage={subsRowsPerPage}
            page={subsPage}
            onPageChange={(e, newPage) => setSubsPage(newPage)}
            onRowsPerPageChange={(e) => {
              setSubsRowsPerPage(parseInt(e.target.value, 10));
              setSubsPage(0);
            }}
          />
        </Paper>
      )}

      {/* TAB 2: Payment & Transaction History */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Typography variant="h6" fontWeight="medium">
              System Payments & Transaction History
            </Typography>
            <TextField
              size="small"
              placeholder="Search by instructor, email, or transaction ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 350 } }}
            />
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell><b>Instructor</b></TableCell>
                  <TableCell><b>Email</b></TableCell>
                  <TableCell><b>Transaction ID</b></TableCell>
                  <TableCell><b>Method</b></TableCell>
                  <TableCell><b>Amount</b></TableCell>
                  <TableCell><b>Expiry Date</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Notes</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No transactions found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPayments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>{new Date(payment.paymentDate).toLocaleDateString()} {new Date(payment.paymentDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell><b>{payment.instructorName}</b></TableCell>
                      <TableCell>{payment.instructorEmail}</TableCell>
                      <TableCell><code>{payment.transactionId || 'N/A'}</code></TableCell>
                      <TableCell>
                        <Chip
                          label={payment.paymentMethod}
                          size="small"
                          variant="outlined"
                          color={payment.paymentMethod === 'FreeTrial' ? 'info' : payment.paymentMethod === 'BankTransfer' ? 'warning' : 'primary'}
                        />
                      </TableCell>
                      <TableCell><b>£{payment.amount.toFixed(2)}</b></TableCell>
                      <TableCell>
                        {payment.expiryDate ? new Date(payment.expiryDate).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.paymentStatus}
                          color={payment.paymentStatus === 'Completed' ? 'success' : payment.paymentStatus === 'Pending' ? 'warning' : 'error'}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {payment.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredPayments.length}
            rowsPerPage={paymentsRowsPerPage}
            page={paymentsPage}
            onPageChange={(e, newPage) => setPaymentsPage(newPage)}
            onRowsPerPageChange={(e) => {
              setPaymentsRowsPerPage(parseInt(e.target.value, 10));
              setPaymentsPage(0);
            }}
          />
        </Paper>
      )}

      {/* TAB 3: Testimonials Moderation */}
      {activeTab === 3 && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" fontWeight="medium" mb={3}>
            Testimonials Moderation
          </Typography>

          {testimonialsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress />
            </Box>
          ) : testimonials.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No testimonials have been submitted yet.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell><b>Author Name</b></TableCell>
                    <TableCell><b>Role</b></TableCell>
                    <TableCell><b>Rating</b></TableCell>
                    <TableCell><b>Content</b></TableCell>
                    <TableCell><b>Status</b></TableCell>
                    <TableCell><b>Date Submitted</b></TableCell>
                    <TableCell align="right"><b>Actions</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {testimonials.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell><b>{t.authorName}</b></TableCell>
                      <TableCell>
                        <Chip 
                          label={t.role} 
                          color={t.role === 'Instructor' ? 'primary' : 'secondary'} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Rating value={t.rating} readOnly size="small" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {t.content}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t.isApproved ? 'Approved' : 'Pending Approval'}
                          color={t.isApproved ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(t.createdAt).toLocaleDateString('en-GB')}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gap={1}>
                          {!t.isApproved && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => handleApproveTestimonial(t.id)}
                              sx={{ fontWeight: 'bold', borderRadius: 1.5 }}
                            >
                              Approve
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleRejectTestimonial(t.id)}
                            sx={{ fontWeight: 'bold', borderRadius: 1.5 }}
                          >
                            {t.isApproved ? 'Remove' : 'Reject'}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {activeTab === 4 && (
        <Grid container spacing={3}>
          {/* Create Route Column */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="medium">
                Create Driving Test Route
              </Typography>
              <Alert severity="info" sx={{ py: 0.5, px: 1.5, fontSize: 13 }}>
                Fill out the route details, then click on the grid on the right sequentially to record the path coordinates.
              </Alert>
              <form onSubmit={handleCreateRoute} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <TextField
                  label="Route Name"
                  size="small"
                  required
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  placeholder="e.g. Chingford Route 1"
                  fullWidth
                />
                <TextField
                  label="Test Centre Name"
                  size="small"
                  required
                  value={routeCenter}
                  onChange={(e) => setRouteCenter(e.target.value)}
                  placeholder="e.g. Chingford Test Centre"
                  fullWidth
                />
                <TextField
                  label="Starting Location"
                  size="small"
                  required
                  value={routeStart}
                  onChange={(e) => setRouteStart(e.target.value)}
                  placeholder="e.g. Station Road"
                  fullWidth
                />
                <TextField
                  label="Route Description"
                  size="small"
                  multiline
                  rows={3}
                  value={routeDesc}
                  onChange={(e) => setRouteDesc(e.target.value)}
                  placeholder="e.g. Features double-roundabout exits and a hill start on a 40mph dual carriageway."
                  fullWidth
                />
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Recorded Path Nodes: {recordedCoords.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Create the track path by clicking on the grid map coordinate system.
                  </Typography>
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={creatingRoute || recordedCoords.length === 0}
                  sx={{ py: 1.2, fontWeight: 'bold', borderRadius: 2, textTransform: 'none' }}
                >
                  {creatingRoute ? 'Saving Route...' : 'Save Test Route'}
                </Button>
              </form>
            </Paper>
          </Grid>

          {/* Interactive Recording Map Column */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Box mb={3}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Coordinate Grid Track Recorder
              </Typography>
              <TestRoutesMap
                coordinates={recordedCoords}
                tips={[]}
                isAdminRecording={true}
                onRecordCoordinate={(coord) => setRecordedCoords((prev) => [...prev, coord])}
                onClearRecorded={() => setRecordedCoords([])}
              />
            </Box>

            {/* List of Existing Routes */}
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="medium" mb={2.5}>
                Active Test Routes ({adminRoutes.length})
              </Typography>
              {adminRoutes.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                  No test routes defined yet. Record one using the coordinates grid above!
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell><b>Route Name</b></TableCell>
                        <TableCell><b>Test Centre</b></TableCell>
                        <TableCell><b>Start Location</b></TableCell>
                        <TableCell align="right"><b>Actions</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adminRoutes.map((route) => (
                        <TableRow key={route.id} hover>
                          <TableCell sx={{ fontWeight: 'semibold' }}>{route.routeName}</TableCell>
                          <TableCell>{route.centerName}</TableCell>
                          <TableCell>{route.startLocation}</TableCell>
                          <TableCell align="right">
                            <IconButton color="error" onClick={() => handleDeleteRoute(route.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Edit Subscription Dialog */}
      <Dialog open={dialogOpen} onClose={() => !savingSub && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Edit Subscription for {editingSub?.firstName} {editingSub?.lastName}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel id="plan-type-label">Subscription Plan Type</InputLabel>
                <Select
                  labelId="plan-type-label"
                  value={subType}
                  label="Subscription Plan Type"
                  onChange={(e) => {
                    setSubType(e.target.value);
                    // Autofill date when plan shifts
                    if (e.target.value === 'Monthly') {
                      setSubExpiryDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                    } else if (e.target.value === 'Annual') {
                      setSubExpiryDate(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                    } else if (e.target.value === 'Trial') {
                      setSubExpiryDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
                    } else {
                      setSubExpiryDate('');
                    }
                  }}
                >
                  <MenuItem value="None">None (No Active Subscription)</MenuItem>
                  <MenuItem value="Trial">Trial (Free / 1-Month Trial)</MenuItem>
                  <MenuItem value="Monthly">Monthly Plan (Base: £10.00 / Premium: £15.00)</MenuItem>
                  <MenuItem value="Annual">Annual Plan (Base: £99.00 / Premium: £149.00)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {subType !== 'None' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Expiration Date"
                  type="date"
                  value={subExpiryDate}
                  onChange={(e) => setSubExpiryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <Box display="flex" gap={1} sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      let baseDate = new Date();
                      if (subExpiryDate) {
                        const parsed = new Date(subExpiryDate);
                        if (!isNaN(parsed.getTime()) && parsed > baseDate) {
                          baseDate = parsed;
                        }
                      }
                      baseDate.setDate(baseDate.getDate() + 30);
                      setSubExpiryDate(baseDate.toISOString().split('T')[0]);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    +1 Month
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      let baseDate = new Date();
                      if (subExpiryDate) {
                        const parsed = new Date(subExpiryDate);
                        if (!isNaN(parsed.getTime()) && parsed > baseDate) {
                          baseDate = parsed;
                        }
                      }
                      baseDate.setFullYear(baseDate.getFullYear() + 1);
                      setSubExpiryDate(baseDate.toISOString().split('T')[0]);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    +1 Year
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setSubExpiryDate(new Date().toISOString().split('T')[0]);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 'bold', ml: 'auto' }}
                  >
                    Reset to Today
                  </Button>
                </Box>
              </Grid>
            )}

            {subType !== 'None' && (
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasWhatsApp}
                      onChange={(e) => setHasWhatsApp(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <b>Enable WhatsApp Messaging Module</b>
                      <Chip label="+£5/mo or +£50/yr" size="small" color="secondary" variant="outlined" />
                    </Box>
                  }
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Adjustment Reason / Notes"
                placeholder="e.g., Extended free trial for marketing, manual cash payment received, etc."
                multiline
                rows={3}
                value={subNotes}
                onChange={(e) => setSubNotes(e.target.value)}
              />
            </Grid>

            {editingSub?.paymentHistory && editingSub.paymentHistory.length > 0 && (
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1a237e' }}>
                  Payment History ({editingSub.paymentHistory.length} Transactions)
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Date</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Method</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Amount</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Expiry</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Status</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', fontSize: '11px' }}>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {editingSub.paymentHistory.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                            {new Date(p.paymentDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px' }}>{p.paymentMethod}</TableCell>
                          <TableCell sx={{ fontSize: '11px', fontWeight: 'bold' }}>
                            £{p.amount.toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                            {p.expiryDate ? new Date(p.expiryDate).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px' }}>
                            <Chip 
                              label={p.paymentStatus} 
                              size="small" 
                              color={p.paymentStatus === 'Completed' ? 'success' : p.paymentStatus === 'Pending' ? 'warning' : 'error'}
                              sx={{ fontSize: '9px', height: '18px' }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px', color: 'gray', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.notes || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={savingSub} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSaveSubscription}
            disabled={savingSub || (subType !== 'None' && !subExpiryDate)}
            variant="contained"
            color="success"
            sx={{ fontWeight: 'bold' }}
          >
            {savingSub ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Viewer Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { borderRadius: 4, minHeight: '60vh' }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#1a237e', color: '#fff', py: 2.5, px: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar 
              src={selectedUserDetails?.user?.profileImageUrl ? getImageUrl(selectedUserDetails.user.profileImageUrl) : ''}
              sx={{ width: 48, height: 48, bgcolor: 'secondary.main', border: '2px solid #fff' }}
            >
              {selectedUserDetails?.user?.firstName?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {selectedUserDetails?.user ? `${selectedUserDetails.user.firstName} ${selectedUserDetails.user.lastName}` : 'Loading...'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {selectedUserDetails?.user?.email} | Role: {selectedUserDetails?.user?.role || 'User'}
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={selectedUserDetails?.user?.isActive ? 'Active' : 'Inactive'} 
            color={selectedUserDetails?.user?.isActive ? 'success' : 'default'} 
            size="small" 
            sx={{ fontWeight: 'bold', bgcolor: selectedUserDetails?.user?.isActive ? '#2e7d32' : 'gray', color: '#fff' }}
          />
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {detailsLoading ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={8} gap={2}>
              <CircularProgress size={48} />
              <Typography color="textSecondary">Fetching detailed records from database...</Typography>
            </Box>
          ) : selectedUserDetails ? (
            <Box>
              {/* Basic Meta Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" color="textSecondary">Phone Number</Typography>
                    <Typography variant="body2" fontWeight="bold">{selectedUserDetails.user.phoneNumber || 'N/A'}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" color="textSecondary">Date of Birth</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedUserDetails.user.dateOfBirth ? new Date(selectedUserDetails.user.dateOfBirth).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" color="textSecondary">Member Since</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedUserDetails.user.createdAt ? new Date(selectedUserDetails.user.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Instructor Details */}
              {selectedUserDetails.user.role === 'Instructor' && selectedUserDetails.details && (
                <Box>
                  <Tabs 
                    value={detailsActiveTab} 
                    onChange={(_, val) => setDetailsActiveTab(val)}
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                  >
                    <Tab label="Directory Profile" />
                    <Tab label={`Students (${selectedUserDetails.details.students?.length || 0})`} />
                    <Tab label={`Expenses (${selectedUserDetails.details.expenses?.length || 0})`} />
                    <Tab label={`Billing Subscriptions (${selectedUserDetails.details.payments?.length || 0})`} />
                  </Tabs>

                  {/* Instructor Tab 0: Profile */}
                  {detailsActiveTab === 0 && (
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 7 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">Biography / Profile Info</Typography>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minHeight: 100, mb: 3 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {selectedUserDetails.details.profileInfo || 'No biography details provided.'}
                          </Typography>
                        </Paper>

                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">Vehicle Image Gallery</Typography>
                        {selectedUserDetails.details.vehicleImageUrls && selectedUserDetails.details.vehicleImageUrls.length > 0 ? (
                          <Grid container spacing={1}>
                            {selectedUserDetails.details.vehicleImageUrls.split(';').filter(Boolean).map((imgUrl: string, idx: number) => (
                              <Grid size={{ xs: 4 }} key={idx}>
                                <Box 
                                  component="img"
                                  src={getImageUrl(imgUrl)}
                                  alt={`Vehicle ${idx + 1}`}
                                  sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 2, border: '1px solid #e2e8f0' }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Typography variant="body2" color="textSecondary">No vehicle images uploaded.</Typography>
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, md: 5 }}>
                        <Paper sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                          <Typography variant="subtitle2" fontWeight="bold" mb={2} color="primary">Professional Details</Typography>
                          <Box display="flex" flexDirection="column" gap={1.5}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">ADI License Number:</Typography>
                              <Typography variant="body2" fontWeight="bold">{selectedUserDetails.details.licenseNumber}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">License Expiration:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {selectedUserDetails.details.licenseExpiryDate ? new Date(selectedUserDetails.details.licenseExpiryDate).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">Base Hourly Rate:</Typography>
                              <Typography variant="body2" fontWeight="bold" color="success.main">£{selectedUserDetails.details.baseHourlyRate}/hr</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">Gearbox Transmission:</Typography>
                              <Typography variant="body2" fontWeight="bold">{selectedUserDetails.details.transmissionType}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">Categories Offered:</Typography>
                              <Typography variant="body2" fontWeight="bold">{selectedUserDetails.details.licenseTypes}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="textSecondary">Marketplace Featured:</Typography>
                              <Chip 
                                label={selectedUserDetails.details.isFeatured ? 'Featured Boosting' : 'Standard'} 
                                size="small" 
                                color={selectedUserDetails.details.isFeatured ? 'warning' : 'default'}
                                sx={{ fontWeight: 'bold', height: 20 }}
                              />
                            </Box>
                          </Box>
                          
                          <Typography variant="subtitle2" fontWeight="bold" mt={3} mb={1} color="primary">Served Outcodes</Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {selectedUserDetails.details.outcodes && selectedUserDetails.details.outcodes.length > 0 ? (
                              selectedUserDetails.details.outcodes.map((code: string) => (
                                <Chip key={code} label={code} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
                              ))
                            ) : (
                              <Typography variant="caption" color="textSecondary">No service areas set yet.</Typography>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}

                  {/* Instructor Tab 1: Linked Students */}
                  {detailsActiveTab === 1 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell><b>Student Number</b></TableCell>
                            <TableCell><b>Name</b></TableCell>
                            <TableCell><b>Email Address</b></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!selectedUserDetails.details.students || selectedUserDetails.details.students.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                No students currently assigned to this instructor.
                              </TableCell>
                            </TableRow>
                          ) : (
                            selectedUserDetails.details.students.map((student: any) => (
                              <TableRow key={student.id} hover>
                                <TableCell><code>{student.studentNumber}</code></TableCell>
                                <TableCell><b>{student.firstName} {student.lastName}</b></TableCell>
                                <TableCell>{student.email}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Instructor Tab 2: Expenses */}
                  {detailsActiveTab === 2 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell><b>Date</b></TableCell>
                            <TableCell><b>Category</b></TableCell>
                            <TableCell><b>Amount</b></TableCell>
                            <TableCell><b>Description</b></TableCell>
                            <TableCell align="right"><b>Receipt</b></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!selectedUserDetails.details.expenses || selectedUserDetails.details.expenses.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                No expenses recorded by this instructor.
                              </TableCell>
                            </TableRow>
                          ) : (
                            selectedUserDetails.details.expenses.map((expense: any) => (
                              <TableRow key={expense.id} hover>
                                <TableCell>{new Date(expense.expenseDate).toLocaleDateString()}</TableCell>
                                <TableCell>
                                  <Chip label={expense.category} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell><b>£{expense.amount.toFixed(2)}</b></TableCell>
                                <TableCell>{expense.description || '-'}</TableCell>
                                <TableCell align="right">
                                  {expense.receiptImageUrl ? (
                                    <Button 
                                      size="small" 
                                      variant="outlined" 
                                      href={getImageUrl(expense.receiptImageUrl)} 
                                      target="_blank"
                                      sx={{ py: 0.1, textTransform: 'none' }}
                                    >
                                      View File
                                    </Button>
                                  ) : (
                                    <Typography variant="caption" color="textSecondary">None</Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Instructor Tab 3: Payments */}
                  {detailsActiveTab === 3 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell><b>Date</b></TableCell>
                            <TableCell><b>Method</b></TableCell>
                            <TableCell><b>Transaction ID</b></TableCell>
                            <TableCell><b>Amount</b></TableCell>
                            <TableCell><b>Expiry Date</b></TableCell>
                            <TableCell><b>Status</b></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!selectedUserDetails.details.payments || selectedUserDetails.details.payments.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                No payment history found.
                              </TableCell>
                            </TableRow>
                          ) : (
                            selectedUserDetails.details.payments.map((pm: any) => (
                              <TableRow key={pm.id} hover>
                                <TableCell>{new Date(pm.paymentDate).toLocaleDateString()}</TableCell>
                                <TableCell><Chip label={pm.paymentMethod} size="small" variant="outlined" /></TableCell>
                                <TableCell><code>{pm.transactionId || 'N/A'}</code></TableCell>
                                <TableCell><b>£{pm.amount.toFixed(2)}</b></TableCell>
                                <TableCell>{pm.expiryDate ? new Date(pm.expiryDate).toLocaleDateString() : 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={pm.paymentStatus} 
                                    size="small" 
                                    color={pm.paymentStatus === 'Completed' ? 'success' : pm.paymentStatus === 'Pending' ? 'warning' : 'error'}
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Student Details */}
              {selectedUserDetails.user.role === 'Student' && selectedUserDetails.details && (
                <Box>
                  <Tabs 
                    value={detailsActiveTab} 
                    onChange={(_, val) => setDetailsActiveTab(val)}
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                  >
                    <Tab label="Student Details" />
                    <Tab label={`Lesson History (${selectedUserDetails.details.lessons?.length || 0})`} />
                    <Tab label={`Mock Theory Exams (${selectedUserDetails.details.exams?.length || 0})`} />
                  </Tabs>

                  {/* Student Tab 0: Details */}
                  {detailsActiveTab === 0 && (
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc', height: '100%' }}>
                          <Typography variant="subtitle2" fontWeight="bold" mb={2} color="primary">License & Drive Configuration</Typography>
                          <Box display="flex" flexDirection="column" gap={2}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary">Student Number:</Typography>
                              <Typography variant="body2" fontWeight="bold"><code>{selectedUserDetails.details.studentNumber}</code></Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary">DVSA License Number:</Typography>
                              <Typography variant="body2" fontWeight="bold">{selectedUserDetails.details.licenseNumber || 'Not provided'}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary">License Expiration:</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {selectedUserDetails.details.licenseExpiryDate ? new Date(selectedUserDetails.details.licenseExpiryDate).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary">DVLA Share Code:</Typography>
                              <Typography variant="body2" fontWeight="bold"><code>{selectedUserDetails.details.dvlaShareCode || 'N/A'}</code></Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary">License Verification Status:</Typography>
                              <Chip 
                                label={selectedUserDetails.details.licenseStatus} 
                                color={selectedUserDetails.details.licenseStatus === 'Verified' ? 'success' : selectedUserDetails.details.licenseStatus === 'Rejected' ? 'error' : 'warning'} 
                                size="small" 
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary">Gearbox Type:</Typography>
                              <Typography variant="body2" fontWeight="bold" color="primary">{selectedUserDetails.details.transmissionType}</Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#f8fafc', height: '100%' }}>
                          <Typography variant="subtitle2" fontWeight="bold" mb={2} color="primary">Assigned Instructor & Health Details</Typography>
                          <Box display="flex" flexDirection="column" gap={2}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary">Assigned Instructor:</Typography>
                              <Typography variant="body2" fontWeight="bold" color="primary.main">{selectedUserDetails.details.instructorName}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary">Emergency Contact Name:</Typography>
                              <Typography variant="body2" fontWeight="bold">{selectedUserDetails.details.emergencyContactName || 'N/A'}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="textSecondary">Emergency Contact Phone:</Typography>
                              <Typography variant="body2" fontWeight="bold">{selectedUserDetails.details.emergencyContactPhone || 'N/A'}</Typography>
                            </Box>
                            <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
                              <Typography variant="body2" color="textSecondary">Medical Conditions:</Typography>
                              <Typography variant="body2" sx={{ fontStyle: selectedUserDetails.details.medicalConditions ? 'normal' : 'italic', color: selectedUserDetails.details.medicalConditions ? 'text.primary' : 'gray' }}>
                                {selectedUserDetails.details.medicalConditions || 'No conditions declared.'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}

                  {/* Student Tab 1: Lesson History */}
                  {detailsActiveTab === 1 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell><b>Date & Time</b></TableCell>
                            <TableCell><b>Duration</b></TableCell>
                            <TableCell><b>Topic</b></TableCell>
                            <TableCell><b>Status</b></TableCell>
                            <TableCell><b>Instructor Notes</b></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!selectedUserDetails.details.lessons || selectedUserDetails.details.lessons.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                No lesson history recorded.
                              </TableCell>
                            </TableRow>
                          ) : (
                            selectedUserDetails.details.lessons.map((lesson: any) => (
                              <TableRow key={lesson.id} hover>
                                <TableCell>{new Date(lesson.lessonDate).toLocaleString()}</TableCell>
                                <TableCell>{lesson.duration} minutes</TableCell>
                                <TableCell><b>{lesson.topicName || 'General Instruction'}</b></TableCell>
                                <TableCell>
                                  <Chip 
                                    label={lesson.isCompleted ? 'Completed' : lesson.isApproved ? 'Approved' : 'Pending Approval'} 
                                    size="small" 
                                    color={lesson.isCompleted ? 'success' : lesson.isApproved ? 'primary' : 'warning'} 
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </TableCell>
                                <TableCell>{lesson.notes || '-'}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Student Tab 2: Theory exams */}
                  {detailsActiveTab === 2 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell><b>Date Taken</b></TableCell>
                            <TableCell><b>Exam Type</b></TableCell>
                            <TableCell><b>Result Status</b></TableCell>
                            <TableCell><b>Details / Notes</b></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!selectedUserDetails.details.exams || selectedUserDetails.details.exams.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                No theory or practical mock exams recorded.
                              </TableCell>
                            </TableRow>
                          ) : (
                            selectedUserDetails.details.exams.map((exam: any) => (
                              <TableRow key={exam.id} hover>
                                <TableCell>{new Date(exam.examDate).toLocaleDateString()}</TableCell>
                                <TableCell><Chip label={exam.examType} size="small" color="secondary" variant="outlined" /></TableCell>
                                <TableCell>
                                  <Chip 
                                    label={exam.result} 
                                    size="small" 
                                    color={exam.result === 'Pass' ? 'success' : exam.result === 'Fail' ? 'error' : 'default'}
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </TableCell>
                                <TableCell>{exam.notes || '-'}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load user details.</Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setDetailsDialogOpen(false)} variant="outlined" color="primary" sx={{ fontWeight: 'bold', borderRadius: 2 }}>
            Close Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* BULK IMPORT DIALOG */}
      <Dialog 
        open={openImportDialog} 
        onClose={() => !importing && setOpenImportDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #e2e8f0' }}>
          Bulk Import UK Driving Instructors
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {importError && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {importError}
            </Alert>
          )}
          {importSuccess && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {importSuccess}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can import multiple driving instructors as unclaimed directory profiles. 
            Choose a file (.csv or .json) or paste the data directly in the textbox below.
          </Typography>

          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              component="label"
              sx={{ fontWeight: 'bold', textTransform: 'none' }}
            >
              Upload CSV or JSON File
              <input
                type="file"
                accept=".csv,.json"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
          </Box>

          <TextField
            multiline
            rows={10}
            fullWidth
            placeholder={`Paste JSON array or CSV text here...

Example CSV format:
firstName,lastName,email,phoneNumber,baseHourlyRate,transmissionType,licenseTypes,outcodes,bio,gender
John,Doe,john.doe@example.com,+447700900077,28.00,Manual,B,M1;M2;M3,"Experienced instructor",Male`}
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            disabled={importing}
            variant="outlined"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              '& .MuiInputBase-input': { fontFamily: 'monospace' }
            }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Note: Commas in CSV values must be quoted. Outcodes should be separated by a semicolon (;) in CSV. 
            Imported accounts will be created as <b>Inactive</b>, allowing them to claim their profile later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button 
            onClick={() => setOpenImportDialog(false)} 
            variant="outlined" 
            color="inherit" 
            disabled={importing}
            sx={{ fontWeight: 'bold', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImport} 
            variant="contained" 
            color="primary" 
            disabled={importing || !importText.trim()}
            sx={{ fontWeight: 'bold', borderRadius: 2 }}
          >
            {importing ? <CircularProgress size={24} color="inherit" /> : 'Run Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
